/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponse,
  BEResponseListCertificato,
  BEResponseListFirmaResponseDTO,
  BEResponseListWSEsternoType,
  FirmaRequest,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Consente di richiedere al provider di firma la generazione ed invio del OTP
 * @summary Generazione ed invio OTP
 */
export const postBackFirmaRemotaOtpId = (
  idWsEsterno: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponse>(
    {
      url: `/framework-bo-procedimenti/firma-remota/otp/${idWsEsterno}`,
      method: 'post',
    },
    options,
  );
};

export const getPostBackFirmaRemotaOtpIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackFirmaRemotaOtpId>>,
    TError,
    { idWsEsterno: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackFirmaRemotaOtpId>>,
  TError,
  { idWsEsterno: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackFirmaRemotaOtpId>>,
    { idWsEsterno: string }
  > = props => {
    const { idWsEsterno } = props ?? {};

    return postBackFirmaRemotaOtpId(idWsEsterno, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackFirmaRemotaOtpIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackFirmaRemotaOtpId>>
>;

export type PostBackFirmaRemotaOtpIdMutationError = ErrorType<unknown>;

/**
 * @summary Generazione ed invio OTP
 */
export const usePostBackFirmaRemotaOtpId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackFirmaRemotaOtpId>>,
    TError,
    { idWsEsterno: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostBackFirmaRemotaOtpIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Consente di firma uno o più documento
 * @summary Firma remota documenti
 */
export const postBackFirmaRemotaFirmaRemotaId = (
  idWSEsterno: string,
  firmaRequest: FirmaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseListFirmaResponseDTO>(
    {
      url: `/framework-bo-procedimenti/firma-remota/firma-remota/${idWSEsterno}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: firmaRequest,
    },
    options,
  );
};

export const getPostBackFirmaRemotaFirmaRemotaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackFirmaRemotaFirmaRemotaId>>,
    TError,
    { idWSEsterno: string; data: FirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackFirmaRemotaFirmaRemotaId>>,
  TError,
  { idWSEsterno: string; data: FirmaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackFirmaRemotaFirmaRemotaId>>,
    { idWSEsterno: string; data: FirmaRequest }
  > = props => {
    const { idWSEsterno, data } = props ?? {};

    return postBackFirmaRemotaFirmaRemotaId(idWSEsterno, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackFirmaRemotaFirmaRemotaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackFirmaRemotaFirmaRemotaId>>
>;
export type PostBackFirmaRemotaFirmaRemotaIdMutationBody = FirmaRequest;
export type PostBackFirmaRemotaFirmaRemotaIdMutationError = ErrorType<unknown>;

/**
 * @summary Firma remota documenti
 */
export const usePostBackFirmaRemotaFirmaRemotaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackFirmaRemotaFirmaRemotaId>>,
    TError,
    { idWSEsterno: string; data: FirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackFirmaRemotaFirmaRemotaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna la lista dei provider di firma configurati a sistema
 * @summary Lista dei provider di firma
 */
export const getBackFirmaRemotaListaProviderFirma = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListWSEsternoType>(
    {
      url: `/framework-bo-procedimenti/firma-remota/lista-provider-firma`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackFirmaRemotaListaProviderFirmaQueryKey = () =>
  [`/framework-bo-procedimenti/firma-remota/lista-provider-firma`] as const;

export const getGetBackFirmaRemotaListaProviderFirmaQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackFirmaRemotaListaProviderFirmaQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>
  > = ({ signal }) =>
    getBackFirmaRemotaListaProviderFirma(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackFirmaRemotaListaProviderFirmaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>
>;
export type GetBackFirmaRemotaListaProviderFirmaQueryError = ErrorType<unknown>;

/**
 * @summary Lista dei provider di firma
 */
export const useGetBackFirmaRemotaListaProviderFirma = <
  TData = Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackFirmaRemotaListaProviderFirma>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackFirmaRemotaListaProviderFirmaQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna la lista dei certificati di firma associati all'utenza fornita in header per un particolare provider
 * @summary Lista certificati di firma
 */
export const getBackFirmaRemotaListaCertificatiId = (
  idWSEsterno: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListCertificato>(
    {
      url: `/framework-bo-procedimenti/firma-remota/lista-certificati/${idWSEsterno}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackFirmaRemotaListaCertificatiIdQueryKey = (
  idWSEsterno: string,
) =>
  [
    `/framework-bo-procedimenti/firma-remota/lista-certificati/${idWSEsterno}`,
  ] as const;

export const getGetBackFirmaRemotaListaCertificatiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>,
  TError = ErrorType<unknown>,
>(
  idWSEsterno: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackFirmaRemotaListaCertificatiIdQueryKey(idWSEsterno);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>
  > = ({ signal }) =>
    getBackFirmaRemotaListaCertificatiId(idWSEsterno, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idWSEsterno, ...queryOptions };
};

export type GetBackFirmaRemotaListaCertificatiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>
>;
export type GetBackFirmaRemotaListaCertificatiIdQueryError = ErrorType<unknown>;

/**
 * @summary Lista certificati di firma
 */
export const useGetBackFirmaRemotaListaCertificatiId = <
  TData = Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>,
  TError = ErrorType<unknown>,
>(
  idWSEsterno: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackFirmaRemotaListaCertificatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackFirmaRemotaListaCertificatiIdQueryOptions(
    idWSEsterno,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
